// Step 3a: a week of tenant-local days at a time, in one sideways row, each
// saying whether the guest's choice is free that day; "More days", "← Earlier"
// and a date jump, never past the venue's booking horizon.
//
// Two layouts (state/day-tile-logic.ts decides which):
// - "status": each day is one radio reading Free / Full / Closed / …; the
//   chosen day's time strip and start chips follow under the row (ranged
//   bands: the guest picks any start in a window).
// - "starts": a day of fixed start times lists them IN its tile, two to a
//   row in equal columns, with the length once beside the day's name — one
//   tap picks the day and the start (aria-pressed on the start, aria-current
//   on the day). Up to four show (two rows of two), then "Show more options"
//   opens THAT tile in place to every start ("Show fewer" closes it); never a
//   scrollbar inside a tile, and the other tiles keep their place, content
//   and height (day-tile-logic.ts tileStartsView). A day with no start
//   of its own (Closed, Full, "Ride together", a ranged Free day …) is one
//   button, as before, that shows its words and ways out under the row.

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { dayLabel, isYmd, type DayStatus } from "../../state/availability-logic";
import { dayStatusSpoken, dayStatusText, type DayTile, type TileMode } from "../../state/day-tile-logic";
import { formatHmUnambiguous, ymdOfInstant } from "../../state/tenant-time";
import { HScroller } from "./HScroller";

/** A typed date settles after this pause (a calendar-popup pick too). */
const JUMP_SETTLE_MS = 700;

/**
 * "Jump to a date". Typing a date fires an input event per keystroke, most
 * of them a half-typed day (25/10 passes through 02/09, 05/09 …) — so the
 * field keeps its own text while focused and jumps only to a COMPLETE date
 * inside [min, max], once the typing pauses, on Enter, or on leaving the
 * field. Nothing is clamped or written back while the guest is typing; on
 * blur an unusable entry falls back to the selected day.
 */
function JumpToDate({
  selected,
  minYmd,
  maxYmd,
  disabled,
  onJump,
}: {
  selected: string | null;
  minYmd: string;
  maxYmd: string;
  disabled: boolean;
  onJump: (day: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onJumpRef = useRef(onJump);
  onJumpRef.current = onJump;
  const usable = (v: string | null): v is string => !!v && isYmd(v) && v >= minYmd && v <= maxYmd;
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => cancel, []);
  const commit = (v: string | null) => {
    cancel();
    if (usable(v) && v !== selected) onJumpRef.current(v);
  };
  return (
    <label className="vt-avail-jump">
      <span>Jump to a date</span>
      <input
        type="date"
        min={minYmd}
        max={maxYmd}
        value={draft ?? selected ?? ""}
        disabled={disabled}
        onFocus={() => setDraft(selected ?? "")}
        onChange={(e) => {
          const v = e.target.value;
          setDraft(v);
          cancel();
          if (usable(v)) timer.current = setTimeout(() => commit(v), JUMP_SETTLE_MS);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit(e.currentTarget.value);
          }
        }}
        onBlur={(e) => {
          commit(e.currentTarget.value);
          setDraft(null);
        }}
      />
    </label>
  );
}

/** One day of the "starts" layout that offers its own start times. */
function StartsTile({
  day,
  todayYmd,
  tile,
  on,
  selectedStart,
  tz,
  disabled,
  onPickStart,
  onToggleMore,
}: {
  day: string;
  todayYmd: string;
  tile: Extract<DayTile, { kind: "starts" }>;
  on: boolean;
  selectedStart: number | null;
  tz: string;
  disabled: boolean;
  onPickStart: (day: string, ms: number) => void;
  onToggleMore: (day: string) => void;
}) {
  const id = useId();
  const label = dayLabel(day, todayYmd);
  // After "Show more options" / "Show fewer" the tile has grown or shrunk
  // under the button, which keeps focus: the button (the tile's last line)
  // is brought back into view, clear of the docked Add bar (its
  // scroll-margin-bottom is the bar's height, styles.css), without moving
  // focus — once per press, and only when it is not already clear. An
  // effect, not a layout effect: DayStrip's own layout pass (the row's
  // top alignment and heights) has run by then.
  const showRef = useRef<HTMLButtonElement | null>(null);
  const toggledRef = useRef(false);
  useEffect(() => {
    if (!toggledRef.current) return;
    toggledRef.current = false;
    const btn = showRef.current;
    if (!btn) return;
    const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    btn.scrollIntoView({ block: "nearest", inline: "nearest", behavior: reduce ? "auto" : "smooth" });
  }, [tile.toggle]);
  return (
    <div
      role="group"
      aria-labelledby={tile.lengthText ? `${id}-d ${id}-l` : `${id}-d`}
      aria-current={on ? "true" : undefined}
      data-day={day}
      className={`vt-avail-day vt-avail-day-free vt-avail-day-starts${on ? " vt-avail-day-on" : ""}${tile.expanded ? " vt-avail-day-expanded" : ""}`}
    >
      {/* The day's name and the length share the first row where they fit
          (else the length goes to the tile's foot, in every tile); the start
          buttons then sit two to a row, in equal columns. */}
      <div className="vt-avail-day-head">
        <span className="vt-avail-day-name" aria-hidden="true">{label.relative ?? label.weekdayShort}</span>
        {tile.lengthText && (
          <span id={`${id}-l`} className="vt-avail-day-len">
            {tile.lengthText}
          </span>
        )}
      </div>
      <span className="vt-avail-day-date" aria-hidden="true">{label.dateShort}</span>
      <span id={`${id}-d`} className="vt-avail-sr">
        {`${label.long}${label.relative ? `, ${label.relative.toLowerCase()}` : ""}`}
      </span>
      <div id={`${id}-s`} className="vt-avail-tstarts">
        {tile.shown.map((ms) => {
          const hm = formatHmUnambiguous(ms, tz);
          const nextDay = ymdOfInstant(ms, tz) !== day;
          return (
            <button
              key={ms}
              type="button"
              className="vt-avail-tstart"
              aria-pressed={on && ms === selectedStart}
              aria-disabled={disabled || undefined}
              aria-label={nextDay ? `${hm}, next day` : undefined}
              onClick={() => {
                if (!disabled) onPickStart(day, ms);
              }}
            >
              {hm}
              {nextDay && <small aria-hidden="true">+1</small>}
            </button>
          );
        })}
      </div>
      {/* One button, the same element open or closed, so keyboard focus
          stays on it when it is pressed. */}
      {tile.toggle && (
        <button
          ref={showRef}
          type="button"
          className="vt-avail-tshow"
          aria-expanded={tile.toggle === "fewer"}
          aria-controls={`${id}-s`}
          onClick={() => {
            toggledRef.current = true;
            onToggleMore(day);
          }}
        >
          {tile.toggle === "fewer" ? "Show fewer" : "Show more options"}
          <span className="vt-avail-sr">
            {tile.toggle === "fewer"
              ? ` start times on ${label.short}`
              : ` — ${tile.more} more start time${tile.more === 1 ? "" : "s"} on ${label.short}`}
          </span>
        </button>
      )}
    </div>
  );
}

export function DayStrip({
  days,
  statuses,
  selected,
  todayYmd,
  hasMore,
  canBack,
  minYmd,
  maxYmd,
  disabled,
  onSelect,
  onMore,
  onBack,
  onJump,
  noun,
  mode = "status",
  tiles,
  selectedStart = null,
  tz,
  onPickStart,
  onToggleMore,
}: {
  days: string[];
  statuses: Record<string, DayStatus>;
  selected: string | null;
  todayYmd: string;
  hasMore: boolean;
  canBack: boolean;
  minYmd: string;
  maxYmd: string;
  disabled: boolean;
  onSelect: (day: string) => void;
  onMore: () => void;
  onBack: () => void;
  onJump: (day: string) => void;
  /** The service's guest noun: "Ride together" for riders. */
  noun: { one: string; other: string };
  /** "starts": days of fixed start times list them in the tile (day-tile-logic.ts). */
  mode?: TileMode;
  /** Per day, what its tile shows in the "starts" layout. */
  tiles?: Record<string, DayTile>;
  /** The chosen start (on the selected day). */
  selectedStart?: number | null;
  tz: string;
  /** A start chosen in a tile: that day AND that start, in one tap. */
  onPickStart?: (day: string, ms: number) => void;
  onToggleMore?: (day: string) => void;
}) {
  const tileRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // The length sits beside the day's name where it fits. Where it does not
  // fit in one tile ("TOMORROW" is the longest name), it goes to the foot of
  // EVERY tile (data-len-below), so the tiles keep one height.
  //
  // A tile opened with "Show more options" grows alone: the row then aligns
  // its tiles to the top (data-open) and every tile keeps at least the
  // height the row had with every tile closed (--vt-day-h), so no other
  // tile changes its place, content or height. That height is measured
  // whenever no tile is open; a row that is opened before it was ever
  // measured (a choice beyond the first four, on a new page) takes its
  // closed tiles' tallest.
  const startsRef = useRef<HTMLDivElement | null>(null);
  const closedHRef = useRef<{ key: string; h: number } | null>(null);
  const rowKey = `${days.join(",")}|${mode}`;
  const rowKeyRef = useRef(rowKey);
  rowKeyRef.current = rowKey;
  const fitLength = useCallback(() => {
    const row = startsRef.current;
    if (!row) return;
    row.removeAttribute("data-len-below");
    const wraps = [...row.querySelectorAll(".vt-avail-day-head")].some((h) => {
      const name = h.querySelector(".vt-avail-day-name");
      const len = h.querySelector(".vt-avail-day-len");
      return !!name && !!len && len.getBoundingClientRect().top > name.getBoundingClientRect().bottom - 2;
    });
    if (wraps) row.setAttribute("data-len-below", "");
    const tiles = [...row.children] as HTMLElement[];
    const open = tiles.filter((t) => t.classList.contains("vt-avail-day-expanded"));
    const key = `${rowKeyRef.current}|${Math.round(row.getBoundingClientRect().width)}`;
    if (open.length === 0) {
      row.removeAttribute("data-open");
      row.style.removeProperty("--vt-day-h");
      const h = Math.max(0, ...tiles.map((t) => t.getBoundingClientRect().height));
      if (h > 0) closedHRef.current = { key, h };
      return;
    }
    row.setAttribute("data-open", "");
    let h = closedHRef.current?.key === key ? closedHRef.current.h : 0;
    if (!h) {
      row.style.removeProperty("--vt-day-h");
      h = Math.max(0, ...tiles.filter((t) => !open.includes(t)).map((t) => t.getBoundingClientRect().height));
    }
    if (h > 0) row.style.setProperty("--vt-day-h", `${h}px`);
    else row.style.removeProperty("--vt-day-h");
  }, []);
  // Every render: the names, the length and the starts may have changed.
  useLayoutEffect(fitLength);
  // A new width, and late web fonts, can change what fits. Width only: the
  // heights fitLength changes must not feed back.
  useLayoutEffect(() => {
    const row = startsRef.current;
    if (!row) return;
    let live = true;
    void document.fonts?.ready.then(() => {
      if (live) fitLength();
    });
    if (typeof ResizeObserver === "undefined") {
      return () => {
        live = false;
      };
    }
    let width = row.getBoundingClientRect().width;
    const ro = new ResizeObserver(() => {
      const w = row.getBoundingClientRect().width;
      if (Math.abs(w - width) < 0.5) return;
      width = w;
      fitLength();
    });
    ro.observe(row);
    return () => {
      live = false;
      ro.disconnect();
    };
  }, [mode, fitLength]);
  // Keyboard focus never drops to <body> at the ends of the strip: "More
  // days →" stays focusable at the horizon (aria-disabled), and "← Earlier",
  // gone once the strip is back at today, hands its focus to "More days →".
  const moreRef = useRef<HTMLButtonElement | null>(null);
  const backHadFocusRef = useRef(false);
  const firstDay = days[0] ?? "";
  useEffect(() => {
    if (!backHadFocusRef.current) return;
    backHadFocusRef.current = false;
    const active = document.activeElement;
    if (!canBack && (!active || active === document.body)) moreRef.current?.focus();
  }, [canBack, firstDay]);
  // Roving tabindex: the selected tile (or the first) is the one Tab stop.
  const rovingIndex = selected && days.includes(selected) ? days.indexOf(selected) : 0;
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    let next = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % days.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i - 1 + days.length) % days.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = days.length - 1;
    if (next < 0) return;
    e.preventDefault();
    const d = days[next];
    if (!d) return;
    tileRefs.current[next]?.focus();
    if (!disabled) onSelect(d);
  };
  return (
    <div className="vt-avail-days">
      <HScroller
        className="vt-avail-day-row"
        prevLabel="Show earlier days"
        nextLabel="Show later days"
        revealSelector={mode === "starts" ? '[aria-current="true"]' : '[aria-checked="true"]'}
        revealKey={selected ? `${days[0] ?? ""}|${selected}|${mode}` : null}
        focusRevealSelector="[data-day]"
      >
        {mode === "starts" ? (
          <div
            ref={startsRef}
            role="group"
            aria-label="Days and start times"
            className="vt-avail-day-tiles vt-avail-day-tiles-starts"
          >
            {days.map((d) => {
              const tile = tiles?.[d];
              const on = d === selected;
              if (tile?.kind === "starts") {
                return (
                  <StartsTile
                    key={d}
                    day={d}
                    todayYmd={todayYmd}
                    tile={tile}
                    on={on}
                    selectedStart={selectedStart}
                    tz={tz}
                    disabled={disabled}
                    onPickStart={(day, ms) => onPickStart?.(day, ms)}
                    onToggleMore={(day) => onToggleMore?.(day)}
                  />
                );
              }
              // No start of its own: one button that shows the day's words
              // (and, on a ranged Free day, its time strip) under the row.
              const label = dayLabel(d, todayYmd);
              const status = statuses[d] ?? "idle";
              const statusText = dayStatusText(status, noun);
              return (
                <button
                  key={d}
                  type="button"
                  data-day={d}
                  aria-current={on ? "true" : undefined}
                  aria-busy={status === "checking" || undefined}
                  aria-disabled={disabled || undefined}
                  aria-label={`${label.long}${label.relative ? `, ${label.relative.toLowerCase()}` : ""}${statusText ? `, ${dayStatusSpoken(status, noun)}` : ""}`}
                  className={`vt-avail-day vt-avail-day-${status}${on ? " vt-avail-day-on" : ""}`}
                  onClick={() => {
                    if (!disabled) onSelect(d);
                  }}
                >
                  <span className="vt-avail-day-name">{label.relative ?? label.weekdayShort}</span>
                  <span className="vt-avail-day-date">{label.dateShort}</span>
                  <span className="vt-avail-day-status">
                    <span className="vt-avail-dot" aria-hidden="true" />
                    {statusText}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div role="radiogroup" aria-label="Day" className="vt-avail-day-tiles">
            {days.map((d, i) => {
              const label = dayLabel(d, todayYmd);
              const status = statuses[d] ?? "idle";
              const statusText = dayStatusText(status, noun);
              const on = d === selected;
              return (
                <button
                  key={d}
                  ref={(el) => {
                    tileRefs.current[i] = el;
                  }}
                  type="button"
                  role="radio"
                  data-day={d}
                  aria-checked={on}
                  tabIndex={i === rovingIndex ? 0 : -1}
                  aria-busy={status === "checking" || undefined}
                  aria-label={`${label.long}${label.relative ? `, ${label.relative.toLowerCase()}` : ""}${statusText ? `, ${dayStatusSpoken(status, noun)}` : ""}`}
                  className={`vt-avail-day vt-avail-day-${status}${on ? " vt-avail-day-on" : ""}`}
                  onClick={() => {
                    if (!disabled) onSelect(d);
                  }}
                  onKeyDown={(e) => onKey(e, i)}
                >
                  <span className="vt-avail-day-name">{label.relative ?? label.weekdayShort}</span>
                  <span className="vt-avail-day-date">{label.dateShort}</span>
                  <span className="vt-avail-day-status">
                    <span className="vt-avail-dot" aria-hidden="true" />
                    {statusText}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </HScroller>
      <div className="vt-avail-day-nav">
        {canBack && (
          <button
            type="button"
            className="vt-avail-link"
            onClick={(e) => {
              backHadFocusRef.current = document.activeElement === e.currentTarget;
              onBack();
            }}
            disabled={disabled}
          >
            ← Earlier
          </button>
        )}
        <button
          ref={moreRef}
          type="button"
          className="vt-avail-link"
          onClick={() => {
            if (hasMore) onMore();
          }}
          disabled={disabled}
          aria-disabled={!hasMore || undefined}
        >
          More days →
        </button>
        <JumpToDate selected={selected} minYmd={minYmd} maxYmd={maxYmd} disabled={disabled} onJump={onJump} />
      </div>
    </div>
  );
}
